import React, { useState, useEffect } from 'react';
import { sendContact } from '../services/contactService';
import { Toast } from './common';
import { IconArrowRight, IconCheck } from './Icons';

/* ── WhatsApp SVG Icon ── */
const IconWhatsApp = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15
      -.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475
      -.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52
      .149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207
      -.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372
      -.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2
      5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085
      1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.116.553 4.103 1.523 5.824L.057 23.561
      a.5.5 0 0 0 .614.614l5.701-1.461A11.944 11.944 0 0 0 12 24c6.627 0 12-5.373
      12-12S18.627 0 12 0zm0 21.818a9.79 9.79 0 0 1-4.998-1.366l-.358-.213-3.712.952
      .974-3.63-.234-.373A9.818 9.818 0 1 1 12 21.818z"/>
  </svg>
);

const WHATSAPP_NUMBER = '917038595952';
const GMAIL_TO = 'manojparmar6161@gmail.com';

const HIGHLIGHTS = [
  '5+ Years Of Experience',
  'Professional Full-Stack Software Engineer',
  'Desktop & CRM App Development',
  'Database & Enterprise ERP Systems',
  'Python & Backend Solutions',
];

const ContactUs = ({ content }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [loading, setLoading] = useState(false);

  // Auto-dismiss toast
  useEffect(() => {
    if (status.msg) {
      const t = setTimeout(() => setStatus({ type: '', msg: '' }), 6000);
      return () => clearTimeout(t);
    }
  }, [status.msg]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    /* ── 1. Build WhatsApp message ── */
    const waText = encodeURIComponent(
      `Hello Manoj! 👋\n\nI am looking for a Software Developer.\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Phone:* ${formData.phone || 'N/A'}\n` +
      `*Subject:* ${formData.subject}\n\n` +
      `*Message:*\n${formData.message}`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${waText}`, '_blank');

    /* ── 2. Open Gmail compose ── */
    const mailSubject = encodeURIComponent(`[Portfolio Contact] ${formData.subject}`);
    const mailBody = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone || 'N/A'}\n\n${formData.message}`
    );
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${GMAIL_TO}&su=${mailSubject}&body=${mailBody}`,
      '_blank'
    );

    /* ── 3. Fire & forget backend save (no await on UI) ── */
    sendContact(formData).catch(() => {});

    setStatus({
      type: 'success',
      msg: '✅ WhatsApp & Gmail opened! Your message is on its way to Manoj.'
    });
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setLoading(false);
  };

  return (
    <div>
      <Toast
        type={status.type}
        message={status.msg}
        onClose={() => setStatus({ type: '', msg: '' })}
      />

      <section id="contact" className="contact-area py-50 rel z-1">
        <div className="container">
          <div className="row">
            {/* ── Left Panel ── */}
            <div className="col-lg-4">
              <div className="contact-content-part pt-5 rpt-0 rmb-55 wow fadeInUp delay-0-2s">
                <div className="section-title mb-40">
                  <span className="sub-title mb-15">Get In Touch</span>
                  <h2>Let's Talk For your <span>Next Projects</span></h2>
                  <p>Reach out today and let's collaborate to bring your project to life, making your ideas a reality.</p>
                </div>

                {/* ── Inline-SVG highlight list (no broken CSS icons) ── */}
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {HIGHLIGHTS.map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '28px', height: '28px', minWidth: '28px',
                        borderRadius: '50%', background: 'rgba(56,189,248,0.12)',
                        border: '1.5px solid rgba(56,189,248,0.35)'
                      }}>
                        <IconCheck size={14} color="#38BDF8" />
                      </span>
                      <span style={{ color: '#CBD5E1', fontSize: '15px', fontWeight: 500 }}>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* ── WhatsApp direct CTA ── */}
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello Manoj! I am looking for a Software Developer.')}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    marginTop: '20px', padding: '10px 20px',
                    background: 'linear-gradient(135deg,#25D366,#128C7E)',
                    borderRadius: '12px', color: '#fff',
                    fontWeight: 700, fontSize: '14px', textDecoration: 'none',
                    boxShadow: '0 4px 16px rgba(37,211,102,0.35)'
                  }}
                >
                  <IconWhatsApp size={18} />
                  Chat on WhatsApp
                </a>
              </div>
            </div>

            {/* ── Right Form ── */}
            <div className="col-lg-8">
              <div className="contact-form contact-form-wrap form-style-one wow fadeInUp delay-0-4s">
                <form id="contactForm" onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="name">Full Name</label>
                        <input type="text" name="name" id="name" className="form-control"
                          placeholder="Name" required value={formData.name} onChange={handleChange} />
                        <label htmlFor="name" className="for-icon"><i className="far fa-user"></i></label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input type="email" name="email" id="email" className="form-control"
                          placeholder="Email" required value={formData.email} onChange={handleChange} />
                        <label htmlFor="email" className="for-icon"><i className="far fa-envelope"></i></label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input type="text" name="phone" id="phone" className="form-control"
                          placeholder="Mobile no" value={formData.phone} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="subject">Subject</label>
                        <input type="text" id="subject" name="subject" className="form-control"
                          placeholder="Subject" required value={formData.subject} onChange={handleChange} />
                        <label htmlFor="subject" className="for-icon"><i className="far fa-edit"></i></label>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea name="message" id="message" className="form-control"
                          rows="4" placeholder="write message" required
                          value={formData.message} onChange={handleChange}>
                        </textarea>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-0">
                        <button
                          type="submit"
                          className="btn-executive-primary w-100 py-3 d-inline-flex align-items-center justify-content-center"
                          disabled={loading}
                        >
                          {loading
                            ? 'Opening...'
                            : <><IconWhatsApp size={18} />&nbsp;&nbsp;Send via WhatsApp + Gmail <IconArrowRight className="ms-2" /></>
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-lines">
          <span></span><span></span>
          <span></span><span></span>
          <span></span><span></span>
          <span></span><span></span>
          <span></span><span></span>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;